import { useState } from "react";

import { UserSubscriptionAction, SubscriptionCancelReason } from "common/types";

import { useCurrentDialog } from "client/common/hooks/stackables";
import Alert from "client/common/components/content/Alert";
import ConfirmationDialog from "client/common/components/content/ConfirmationDialog";
import Select from "client/common/components/inputs/Select";

import { useUpdateSubscriptionMutation } from "../../hooks";
import { ClientSubscription, ListSubscription } from "../../types";
import { getSubscriptionCancelReasonOptions } from "../../utils";

import styles from "../../styles/SubscriptionCancelDialog.module.less";

export type SubscriptionCancelDialogProps = {
  subscription: ClientSubscription | ListSubscription;
};

const SubscriptionCancelDialog = ({ subscription }: SubscriptionCancelDialogProps) => {
  const { close } = useCurrentDialog();
  const {
    mutate: updateSubscription,
    isLoading,
    error,
  } = useUpdateSubscriptionMutation(subscription.id, {
    onSuccess: close,
  });

  const [reason, setReason] = useState<SubscriptionCancelReason | "">("");
  const [reasonOther, setReasonOther] = useState<string>("");
  const isReasonOther = reason === SubscriptionCancelReason.Other;

  const canSubmit = !!reason && (!isReasonOther || !!reasonOther.trim());

  const getSubmitTooltipContent = () => {
    if (!reason) {
      return "Please select a reason to continue";
    }
    if (isReasonOther && !reasonOther.trim()) {
      return "Please specify a reason to continue";
    }
    return undefined;
  };

  const handleConfirm = () => {
    if (!canSubmit) {
      return;
    }
    updateSubscription({
      action: UserSubscriptionAction.Cancel,
      cancel_reason: reason,
      cancel_reason_other: isReasonOther ? reasonOther : undefined,
    });
  };

  return (
    <ConfirmationDialog
      title="Confirm autoship cancelation"
      description={
        <div className={styles.description}>
          <Alert variant="warning">
            Canceling this autoship will not cancel any orders currently in progress.
          </Alert>
          Before you go, please tell us why you are canceling:
        </div>
      }
      confirmText="Yes, cancel autoship"
      denyText="No, continue shipments"
      onConfirm={handleConfirm}
      onDeny={close}
      isLoading={isLoading}
      isConfirmDisabled={!canSubmit}
      confirmDisabledTooltipContent={getSubmitTooltipContent()}
      error={error}
    >
      <div className={styles.fields}>
        <Select
          label="Select a cancelation reason"
          value={reason}
          onChange={(r) => setReason(r as SubscriptionCancelReason | "")}
          disabled={isLoading}
        >
          {getSubscriptionCancelReasonOptions().map(({ label, value }) => (
            <Select.Option key={value} value={value}>
              {label}
            </Select.Option>
          ))}
        </Select>
        {isReasonOther && (
          <textarea
            className={styles.textarea}
            placeholder="Please specify your reason for canceling..."
            value={reasonOther}
            onChange={(e) => setReasonOther(e.target.value)}
            disabled={isLoading}
            rows={3}
          />
        )}
      </div>
    </ConfirmationDialog>
  );
};

export default SubscriptionCancelDialog;
